import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';

// Same dark-portal tooltip visual language as AnalyticsHub/components/KpiTargetTooltip.tsx
const createPortalFn: ((node: React.ReactNode, container: Element) => React.ReactPortal) | null =
  Platform.OS === 'web' ? require('react-dom').createPortal : null;

// Shared portal container with KpiTargetTooltip so both mount at most one node
const PORTAL_ID = 'dlo-tooltip-container';

function ensurePortal(): HTMLElement {
  let el = document.getElementById(PORTAL_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = PORTAL_ID;
    Object.assign(el.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '999999',
    });
    document.body.appendChild(el);
  }
  return el;
}

const TW = 220;
const GAP = 8;
const ARROW = 5;
/** Conservative estimate used only to decide whether the tooltip should render above or below the icon. */
const TOOLTIP_H_ESTIMATE = 110;

interface TPos {
  top: number;
  left: number;
  arrowLeft: number;
  placement: 'top' | 'bottom';
}

interface KpiInfoTooltipProps {
  /** Descriptive text shown in the tooltip — matches KPI tile definitions from the mockup. */
  text: string;
}

const KpiInfoTooltipWeb: React.FC<KpiInfoTooltipProps> = ({ text }) => {
  const wrapRef = useRef<View>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const portalRef = useRef<HTMLElement | null>(null);
  const [pos, setPos] = useState<TPos | null>(null);

  useEffect(() => {
    portalRef.current = ensurePortal();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const computePos = useCallback(() => {
    const node = wrapRef.current as any;
    const dom: HTMLElement | null = node?.nodeType === 1 ? node : (node?._nativeTag ?? null);
    if (!dom?.getBoundingClientRect) return;

    const rect = dom.getBoundingClientRect();
    const placement: TPos['placement'] = rect.top - TOOLTIP_H_ESTIMATE - GAP >= 8 ? 'top' : 'bottom';
    const top = placement === 'top' ? rect.top - TOOLTIP_H_ESTIMATE - GAP : rect.bottom + GAP;

    const cx = rect.left + rect.width / 2;
    let left = cx - TW + 16;
    const rightEdge = window.innerWidth - 16;
    if (left + TW > rightEdge) left = rightEdge - TW;
    if (left < 8) left = 8;

    setPos({ top, left, arrowLeft: Math.max(8, Math.min(cx - left - ARROW, TW - 16)), placement });
  }, []);

  const show = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(computePos, 80);
  }, [computePos]);

  const hide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPos(null);
  }, []);

  const tooltip = pos ? (
    <div
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        pointerEvents: 'none',
        width: TW,
        backgroundColor: '#1E2127',
        borderRadius: 5,
        padding: '8px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.30)',
        fontFamily: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
        fontSize: 11,
        lineHeight: '16px',
        color: '#FFFFFF',
        userSelect: 'none',
        overflow: 'visible',
      }}
    >
      {text}
      <div
        style={{
          position: 'absolute',
          [pos.placement === 'top' ? 'bottom' : 'top']: -ARROW,
          left: pos.arrowLeft,
          width: ARROW * 2,
          height: ARROW * 2,
          backgroundColor: '#1E2127',
          transform: 'rotate(45deg)',
        }}
      />
    </div>
  ) : null;

  return (
    <>
      <View
        ref={wrapRef}
        // @ts-ignore — web-only mouse events; invisible to native
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        <MaterialIcons name="info-outline" size={14} color="#ADB5BD" />
      </View>
      {tooltip && portalRef.current && createPortalFn
        ? createPortalFn(tooltip, portalRef.current)
        : null}
    </>
  );
};

/**
 * KPI tile info icon with a hover tooltip describing the metric's definition,
 * calculation, and target — matches the Crewing Analytics mockup copy.
 * Renders a plain static icon on native (no hover concept there).
 */
const KpiInfoTooltip: React.FC<KpiInfoTooltipProps> = ({ text }) => {
  if (Platform.OS !== 'web' || !text) {
    return <MaterialIcons name="info-outline" size={14} color="#ADB5BD" />;
  }
  return <KpiInfoTooltipWeb text={text} />;
};

export default KpiInfoTooltip;
